/**
 * Table slot “event days” — aligned with app `src/component/seatSelection/seatSelectionFormatters.ts`.
 * One key per scheduled occurrence, not every calendar day between start and end.
 */

const JS_DAY_TO_SLOT: Record<number, string> = {
  0: 'sun',
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
};

const SLOT_DAY_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

function nextSlotDayKey(day: string): string {
  const i = SLOT_DAY_ORDER.indexOf(day as (typeof SLOT_DAY_ORDER)[number]);
  if (i < 0) return day;
  return SLOT_DAY_ORDER[(i + 1) % 7];
}

function timeToMin(t: string): number {
  const [hStr, mStr] = t.split(':');
  const m = mStr && mStr.length > 0 ? mStr : '0';
  return parseInt(hStr, 10) * 60 + parseInt(m, 10);
}

function isOvernightRange(start: string, end: string): boolean {
  return timeToMin(end) < timeToMin(start);
}

type EventLike = {
  event_start_date?: string;
  event_end_date?: string;
  event_date_breakdown?: Array<{ start_date?: string; end_date?: string }>;
  details?: { event_start_date?: string; event_date_breakdown?: Array<{ start_date?: string }> };
};

/**
 * One key per **scheduled** day from `event_date_breakdown` (or a single `event_start_date`),
 * not a multi-day range for overnight end times (Thu 6pm → Fri 2am stays `thu` only).
 */
export function getEventDayKeys(event: EventLike | null | undefined): string[] {
  if (!event) return [];
  const breakdown = event?.details?.event_date_breakdown ?? event?.event_date_breakdown;
  const start =
    event?.event_start_date ??
    event?.details?.event_start_date ??
    breakdown?.[0]?.start_date;
  if (!start) return [];

  if (Array.isArray(breakdown) && breakdown.length > 0) {
    const keys = new Set<string>();
    for (const seg of breakdown) {
      const d = seg?.start_date;
      if (d) keys.add(JS_DAY_TO_SLOT[new Date(d).getDay()]);
    }
    return Array.from(keys);
  }

  return [JS_DAY_TO_SLOT[new Date(start).getDay()]];
}

export function slotMatchesEventDayKeys(
  anchorKeys: string[],
  slot: { day: string; startTime?: string; endTime?: string; start_time?: string; end_time?: string },
): boolean {
  if (anchorKeys.length === 0) return true;
  const d = (slot.day || '').toLowerCase();
  if (anchorKeys.some((k) => k.toLowerCase() === d)) return true;
  for (const a of anchorKeys) {
    const al = a.toLowerCase();
    if (d !== nextSlotDayKey(al)) continue;
    const st = slot.startTime ?? slot.start_time ?? '00:00';
    const en = slot.endTime ?? slot.end_time ?? '00:00';
    if (!isOvernightRange(st, en)) continue;
    const sm = timeToMin(st);
    if (sm >= 18 * 60 && sm < 22 * 60) continue;
    if (sm >= 22 * 60 || sm < 4 * 60) return true;
  }
  return false;
}

export function dedupeTimeSlotsByRange<T extends { day: string; startTime: string; endTime: string }>(
  slots: T[],
  eventDayKeys: string[],
): T[] {
  if (slots.length <= 1) return slots;
  const primary = (eventDayKeys[0] ?? '').toLowerCase();
  const byRange = new Map<string, T>();
  for (const ts of slots) {
    const rangeKey = `${ts.startTime}\0${ts.endTime}`;
    const existing = byRange.get(rangeKey);
    if (!existing) {
      byRange.set(rangeKey, ts);
    } else if (primary && ts.day.toLowerCase() === primary && existing.day.toLowerCase() !== primary) {
      byRange.set(rangeKey, ts);
    }
  }
  const out = Array.from(byRange.values());
  out.sort((a, b) => timeToMin(a.startTime) - timeToMin(b.startTime));
  return out;
}
